import './Quantity.scss';

import cn from 'classnames';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import Button from '@/common/components/atoms/Button/Button';
import FieldErrors from '@/common/components/molecules/FieldErrors/FieldErrors';
import { QUANTITY_LIMIT } from '@/common/constants';
import debounce from '@/common/helpers/debounce';
import usePrevious from '@/common/hooks/usePrevious';

import { QuantityDefaultProps, QuantityPropTypes } from './Quantity.proptypes';

const Quantity = forwardRef(
  (
    {
      className,
      context,
      debounceDelay,
      disabled,
      errors,
      id,
      isLoading,
      max,
      onChange,
      quantity,
      readOnly,
      required,
      salesPackaging,
      splitPackaging,
      tabIndex,
      title,
      unit,
      updateOnChange,
      valid,
    },
    ref,
  ) => {
    const { t } = useTranslation();

    const decrementRef = useRef(null);
    const incrementRef = useRef(null);

    const [value, setValue] = useState({
      number: quantity || 1,
      string: quantity.toString() || '1',
    });

    const previousQuantity = usePrevious(quantity);

    const notSplittable = salesPackaging && !splitPackaging;
    const minSize = 0;
    const maxSize = Math.min(QUANTITY_LIMIT, max);

    const formatDecimals = (decimals) =>
      Number.parseFloat(`0.${'0'.repeat(decimals - 1)}1`);

    const countDecimals = (number) => {
      const arrayValue = number.toString().split('.');

      if (arrayValue.length === 2) {
        return arrayValue[1].length;
      }

      return 0;
    };

    const formatQuantity = (number, decimals) => {
      const newValue = Number.parseFloat(number);
      const arrayValue = number.toString().split('.');

      if (number === '') {
        return {
          number: minSize,
          string: '',
        };
      }

      if (newValue > maxSize || Number.isNaN(newValue)) {
        return {
          number: value.number,
          string: value.string,
        };
      }

      if (newValue < minSize) {
        return {
          number: minSize,
          string: minSize.toString(),
        };
      }

      if (arrayValue.length === 2) {
        const stringValue = `${arrayValue[0]}.${arrayValue[1].slice(
          0,
          decimals,
        )}`;

        return {
          number: Number.parseFloat(stringValue),
          string: stringValue,
        };
      }

      return {
        number: Number.parseFloat(arrayValue[0]),
        string: number.toString(),
      };
    };

    const step = useMemo(() => {
      const decimals = countDecimals(
        notSplittable ? salesPackaging : value.string,
      );

      if (notSplittable) {
        return salesPackaging;
      }

      if (decimals === 0) {
        return 1;
      }

      return formatDecimals(decimals);
    }, [notSplittable, salesPackaging, value]);

    const updateQuantity = useCallback(
      (newQuantity) => {
        if (updateOnChange) {
          if (debounceDelay === 0) {
            onChange({
              id,
              quantity: newQuantity.number,
            });
          } else {
            debounce(
              () =>
                onChange({
                  id,
                  quantity: newQuantity.number,
                }),
              debounceDelay,
            );
          }
        }

        setValue(newQuantity);
      },
      [debounceDelay, id, onChange, updateOnChange],
    );

    const calculation = (action, quantity1, quantity2) => {
      switch (action) {
        case 'decrement':
          return quantity1 - quantity2;
        case 'increment':
        default:
          return quantity1 + quantity2;
      }
    };

    const handleIncrementDecrement = (action) => {
      if (quantity < 0) {
        return;
      }

      let newValue = value.number;
      const decimals = countDecimals(
        notSplittable ? salesPackaging : value.string,
      );
      newValue = calculation(action, newValue, step);
      if (newValue <= 0) {
        return;
      }

      updateQuantity(formatQuantity(newValue.toFixed(decimals), decimals));
    };

    const handleChange = (e) => {
      if (quantity < 0) {
        return;
      }

      const newValue = e.target.value.replace(',', '.');
      const decimals = Math.min(countDecimals(newValue), 3);

      if (/^-?\d*\.?\d*$/.test(newValue)) {
        updateQuantity(formatQuantity(newValue, decimals));
      }
    };

    const handleBlur = (e) => {
      if (
        !e.relatedTarget ||
        (!incrementRef.current.contains(e.relatedTarget) &&
          !decrementRef.current.contains(e.relatedTarget))
      ) {
        let newValue = value.number;

        if (notSplittable) {
          newValue = Math.ceil(newValue / salesPackaging) * salesPackaging;
        }

        updateQuantity({
          number: newValue,
          string: newValue.toString(),
        });
      }
    };

    const handleKeyUp = (e) => {
      switch (e.keyCode) {
        case 13:
          return handleBlur(e);
        case 38:
          return handleIncrementDecrement('increment');
        case 40:
          return handleIncrementDecrement('decrement');
        default:
          return null;
      }
    };

    const isDisabled = useMemo(() => {
      const blocked = isLoading || disabled || quantity < 0;

      return {
        disabled: blocked,
        loading: isLoading,
        max: blocked || value.number + step > maxSize,
        min: blocked || value.number - step <= minSize,
      };
    }, [disabled, step, isLoading, maxSize, quantity, value.number]);

    useEffect(() => {
      if (quantity !== previousQuantity) {
        setValue({
          number: quantity,
          string: quantity.toString(),
        });
      }
    }, [previousQuantity, quantity]);

    return (
      <>
        <div
          className={cn('quantity', className, {
            'quantity--disabled': disabled,
            'quantity--readonly': readOnly,
            'quantity--errors': errors,
            'quantity--valid': valid,
          })}
          data-qa-id={context || 'quantity'}
        >
          {title && <span className="quantity__title">{title}</span>}
          <div className="quantity__wrapper" data-qa-id="quantity-wrapper">
            <Button
              className="quantity__action quantity__action--decrement"
              context="quantity-decrement"
              disabled={isDisabled.min}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleIncrementDecrement('decrement');
              }}
              ref={decrementRef}
            >
              -
            </Button>
            <input
              aria-label={t('common:messages:forms.fields.quantity')}
              className="quantity__field"
              data-qa-id={`quantity-field-${
                disabled || isLoading ? 'disabled' : 'active'
              }`}
              data-qa-max={maxSize}
              data-qa-min={minSize}
              data-qa-step={step}
              disabled={isDisabled.disabled}
              id={id}
              onBlur={handleBlur}
              onChange={handleChange}
              onKeyUp={handleKeyUp}
              readOnly={readOnly}
              ref={ref}
              required={required}
              tabIndex={tabIndex}
              type="text"
              value={value.string}
            />
            <Button
              className="quantity__action quantity__action--increment"
              context="quantity-increment"
              disabled={isDisabled.max}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleIncrementDecrement('increment');
              }}
              ref={incrementRef}
            >
              +
            </Button>
          </div>
          {unit && (
            <label htmlFor={id} className="quantity__label">
              {unit}
            </label>
          )}
        </div>
        {errors && (
          <FieldErrors context="quantity-error-field" errors={errors} />
        )}
      </>
    );
  },
);

Quantity.defaultProps = {
  ...QuantityDefaultProps,
};

Quantity.propTypes = {
  ...QuantityPropTypes,
};

export default Quantity;
